import type { RouteRecordRaw } from 'vue-router';
import { ANZEIGENAME, KATEGORIE } from '@/persistence/RechteStorage';
import { createStepGuard } from '@/router/utils.ts';

export const BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES = {
  BASE: 'buergschaftsauftraege',
  LISTE: 'buergschaftsauftraegeListeAuftraggeber',
  DETAILS: 'buergschaftsauftragDetailsAuftraggeber',
  // "Beantragen" Workflow
  BEANTRAGEN_BASE: 'auftraggeber-buergschaftsauftraege-beantragen-base',
  BEANTRAGEN_BASE_DATA: 'auftraggeber-buergschaftsauftraege-beantragen-base-data',
  BEANTRAGEN_VERTRAG_AUSWAHL: 'auftraggeber-buergschaftsauftraege-beantragen-vertrag-auswahl',
  BEANTRAGEN_TEXT_AUSWAHL: 'auftraggeber-buergschaftsauftraege-beantragen-text-auswahl',
  BEANTRAGEN_BUERGSCHAFTSDATEN: 'auftraggeber-buergschaftsauftraege-beantragen-buergschaftsdaten',
  BEANTRAGEN_VERSANDWEG: 'auftraggeber-buergschaftsauftraege-beantragen-versandweg',
  BEANTRAGEN_WEITERE_ANGABEN: 'auftraggeber-buergschaftsauftraege-beantragen-weitere-angaben',
  BEANTRAGEN_ZWEITPRUEFUNG_EINRICHTEN:
    'auftraggeber-buergschaftsauftraege-beantragen-zweitpruefung-einrichten',
  BEANTRAGEN_EINGABEN_PRUEFEN: 'auftraggeber-buergschaftsauftraege-beantragen-eingaben-pruefen',

  // "Zweitpruefen" Workflow
  ZWEITPRUEFEN_BASE: 'auftraggeber-buergschaftsauftraege-zweitpruefen-base',
  ZWEITPRUEFEN_DETAILS: 'auftraggeber-buergschaftsauftraege-zweitpruefen-details',
  ZWEITPRUEFEN_ENTSCHEIDUNG: 'auftraggeber-buergschaftsauftraege-zweitpruefen-entscheidung',
  ZWEITPRUEFEN_AKZEPTIEREN: 'auftraggeber-buergschaftsauftraege-zweitpruefen-akzeptieren',
  ZWEITPRUEFEN_AKZEPTIEREN_MIT_AENDERUNG:
    'auftraggeber-buergschaftsauftraege-zweitpruefen-akzeptieren-mit-aenderung',
  ZWEITPRUEFEN_ABLEHNEN: 'auftraggeber-buergschaftsauftraege-zweitpruefen-ablehnen',
} as const;

export const buergschaftsauftraegeRoutes: RouteRecordRaw = {
  name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BASE,
  path: 'buergschaftsauftraege',
  redirect: { name: 'buergschaftsauftraegeListeAuftraggeber' },
  children: [
    /** Liste View */
    {
      path: 'liste',
      name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.LISTE,
      props: true,
      component: () => import('./views/BuergschaftsauftragListe.vue'),
      meta: {
        kategorie: [KATEGORIE.AUFTRAEGE],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },
    /** Details View */
    {
      path: 'details/:buergschaftsauftragId(\\d+)',
      name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.DETAILS,
      props: true,
      component: () => import('./views/BuergschaftsauftragDetails.vue'),
      meta: {
        kategorie: [KATEGORIE.BÜRGSCHAFTEN],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },

    /** "Beantragen" Workflow */
    {
      name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_BASE,
      path: 'beantragen',
      redirect: { name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_BASE_DATA },
      component: () => import('./views/beantragen/_BaseView.vue'),
      children: [
        {
          name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_BASE_DATA,
          path: 'basis-daten',
          component: () => import('./views/beantragen/BaseDataView.vue'),
        },
        {
          name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_VERTRAG_AUSWAHL,
          path: 'vertrag-auswahl',
          component: () => import('./views/beantragen/VertragAuswahlView.vue'),
          beforeEnter: createStepGuard(
            [
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_BASE_DATA,
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_BUERGSCHAFTSDATEN,
            ],
            BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_BASE_DATA,
          ),
        },
        {
          name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_BUERGSCHAFTSDATEN,
          path: 'buergschaftsdaten',
          component: () => import('./views/beantragen/BuergschaftsdatenView.vue'),
          beforeEnter: createStepGuard(
            [
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_VERTRAG_AUSWAHL,
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_TEXT_AUSWAHL,
            ],
            BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_BASE_DATA,
          ),
        },
        {
          name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_TEXT_AUSWAHL,
          path: 'text-auswahl',
          component: () => import('./views/beantragen/TextAuswahlView.vue'),
          beforeEnter: createStepGuard(
            [
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_BUERGSCHAFTSDATEN,
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_VERSANDWEG,
            ],
            BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_BASE_DATA,
          ),
        },
        {
          name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_VERSANDWEG,
          path: 'versandweg',
          component: () => import('./views/beantragen/VersandwegView.vue'),
          beforeEnter: createStepGuard(
            [
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_TEXT_AUSWAHL,
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_WEITERE_ANGABEN,
            ],
            BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_BASE_DATA,
          ),
        },
        {
          name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_WEITERE_ANGABEN,
          path: 'weitere-angaben',
          component: () => import('./views/beantragen/WeitereAngabenView.vue'),
          beforeEnter: createStepGuard(
            [
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_VERSANDWEG,
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_ZWEITPRUEFUNG_EINRICHTEN,
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_EINGABEN_PRUEFEN,
            ],
            BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_BASE_DATA,
          ),
        },
        {
          name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_ZWEITPRUEFUNG_EINRICHTEN,
          path: 'weitere-angaben',
          component: () => import('./views/beantragen/ZweitpruefungEinrichtenView.vue'),
          beforeEnter: createStepGuard(
            [
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_WEITERE_ANGABEN,
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_EINGABEN_PRUEFEN,
            ],
            BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_BASE_DATA,
          ),
        },
        {
          name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_EINGABEN_PRUEFEN,
          path: 'eingaben-pruefen',
          component: () => import('./views/beantragen/EingabenPruefenView.vue'),
          beforeEnter: createStepGuard(
            [
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_ZWEITPRUEFUNG_EINRICHTEN,
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_WEITERE_ANGABEN,
            ],
            BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.BEANTRAGEN_BASE_DATA,
          ),
        },
      ],
    },

    /** "Zweitpruefen" Workflow */
    {
      name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.ZWEITPRUEFEN_BASE,
      path: ':buergschaftsauftragId(\\d+)/zweitpruefen',
      props: (route) => ({
        buergschaftsauftragId: Number.parseInt(route.params.buergschaftsauftragId as string, 10),
      }),
      redirect: { name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.ZWEITPRUEFEN_DETAILS },
      component: () => import('./views/zweitpruefen/_BaseView.vue'),
      children: [
        {
          name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.ZWEITPRUEFEN_DETAILS,
          path: 'details',
          component: () => import('./views/zweitpruefen/DetailsView.vue'),
        },
        {
          name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.ZWEITPRUEFEN_ENTSCHEIDUNG,
          path: 'entscheidung',
          component: () => import('./views/zweitpruefen/EntscheidungView.vue'),
          beforeEnter: createStepGuard(
            [
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.ZWEITPRUEFEN_DETAILS,
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.ZWEITPRUEFEN_AKZEPTIEREN_MIT_AENDERUNG,
              BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.ZWEITPRUEFEN_ABLEHNEN,
            ],
            BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.ZWEITPRUEFEN_BASE,
          ),
        },
        {
          name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.ZWEITPRUEFEN_AKZEPTIEREN_MIT_AENDERUNG,
          path: 'aendern',
          component: () => import('./views/zweitpruefen/AkzeptierenMitAenderungView.vue'),
          beforeEnter: createStepGuard(
            [BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.ZWEITPRUEFEN_ENTSCHEIDUNG],
            BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.ZWEITPRUEFEN_BASE,
          ),
        },
        {
          name: BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.ZWEITPRUEFEN_ABLEHNEN,
          path: 'ablehnen',
          component: () => import('./views/zweitpruefen/AblehnenView.vue'),
          beforeEnter: createStepGuard(
            [BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.ZWEITPRUEFEN_ENTSCHEIDUNG],
            BUERGSCHAFTSAUFTRAEGE_ROUTE_NAMES.ZWEITPRUEFEN_BASE,
          ),
        },
      ],
    },
  ],
};

export const BEANTRAGEN_QUERY_PARAMS = {
  TEMPLATE_AUFTRAG_ID: 'templateAuftragId',
} as const;
